/**
 * Data access for the cuisine & tag taxonomy (schema.sql §11).
 *
 * Reads are anonymous (public-read RLS); writes (operators adding new
 * vocabulary) require a platform-user session. Mirrors the style of
 * `@/data/restaurants` — thin wrappers over the Supabase client, no mapping.
 */

import { supabase } from "@/lib/supabase";
import type { Cuisine, Tag } from "@/types/taxonomy";

export async function listCuisines(): Promise<Cuisine[]> {
  const { data, error } = await supabase
    .from("cuisines")
    .select("slug, label_en, label_ro, sort")
    .order("sort", { ascending: true });
  if (error) throw error;
  return (data ?? []) as Cuisine[];
}

export async function listTags(): Promise<Tag[]> {
  const { data, error } = await supabase
    .from("tags")
    .select("slug, label_en, label_ro, category, sort")
    .order("sort", { ascending: true });
  if (error) throw error;
  return (data ?? []) as Tag[];
}

/**
 * Turn an English label into a stable, URL-safe slug: lowercase, diacritics
 * stripped, non-alphanumerics collapsed to single dashes. e.g. "Wine bar" →
 * "wine-bar", "Bere artizanală" → "bere-artizanala".
 */
export function slugify(label: string): string {
  return label
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "") // strip combining diacritics
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 48);
}

export interface NewTaxonomyEntry {
  label_en: string;
  label_ro: string;
}

/**
 * Add a cuisine to the shared vocabulary. Slug is derived from the EN label.
 * Idempotent at the UI level: if the slug already exists the existing row is
 * returned instead of erroring, so adding a duplicate just reuses it.
 */
export async function createCuisine(entry: NewTaxonomyEntry): Promise<Cuisine> {
  const slug = slugify(entry.label_en);
  const { data, error } = await supabase
    .from("cuisines")
    .upsert(
      { slug, label_en: entry.label_en.trim(), label_ro: entry.label_ro.trim() },
      { onConflict: "slug", ignoreDuplicates: false },
    )
    .select("slug, label_en, label_ro, sort")
    .single();
  if (error) throw error;
  return data as Cuisine;
}

/** Add a tag to the shared vocabulary (category defaults to null). */
export async function createTag(entry: NewTaxonomyEntry): Promise<Tag> {
  const slug = slugify(entry.label_en);
  const { data, error } = await supabase
    .from("tags")
    .upsert(
      { slug, label_en: entry.label_en.trim(), label_ro: entry.label_ro.trim() },
      { onConflict: "slug", ignoreDuplicates: false },
    )
    .select("slug, label_en, label_ro, category, sort")
    .single();
  if (error) throw error;
  return data as Tag;
}
